import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { useCallback, useState, type ChangeEvent } from "react";
import {
  BenchFireTransactionSet,
  BenchIntegrationType,
  BenchStepOutcome,
  BenchStopRole,
  Edi214ShipmentStatus,
  Edi990ActionCode,
  useFireOutboundBenchTransactionMutation,
  type FireOutboundBenchTransactionMutation,
} from "../../../generated/graphql";
import { throwOnGraphqlErrors } from "../../api-keys-admin/throw-on-graphql-errors";
import { type BenchOrderOption } from "../bench-run";
import { BenchOrderPicker } from "./bench-order-picker";
import { EdiTransactionsTable } from "./bench-report-view";

const styles = {
  row: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 2,
    alignItems: "center",
  } satisfies SxProps<Theme>,
};

const CONTACT_ATTEMPTED: string = Edi214ShipmentStatus.ContactAttempted;

type FireResult =
  FireOutboundBenchTransactionMutation["fireOutboundBenchTransaction"];

type FirePanelProps = {
  readonly partnershipUuid: string | null;
  readonly defaultOrder: BenchOrderOption | null;
  readonly statuses: Edi214ShipmentStatus[];
  readonly disabled: boolean;
  readonly confirmProductionSend: boolean | undefined;
};

/**
 * Fire mode: one 990, 214 or 210 for one order, straight through
 * `EdiOutboundService`. The 214 list comes from the partnership, which is the
 * list the backend checks the status against.
 */
const FirePanel = ({
  partnershipUuid,
  defaultOrder,
  statuses,
  disabled,
  confirmProductionSend,
}: FirePanelProps) => {
  const [order, setOrder] = useState(defaultOrder);
  const [transactionSet, setTransactionSet] = useState(
    BenchFireTransactionSet.Edi_214,
  );
  const [status214, setStatus214] = useState<string>("");
  const [stopRole214, setStopRole214] = useState<string>("");
  const [action990, setAction990] = useState(Edi990ActionCode.Accepted);
  const [allowDuplicate, setAllowDuplicate] = useState(false);
  const [result, setResult] = useState<FireResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fire, { loading }] = useFireOutboundBenchTransactionMutation();

  const handleSetChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setTransactionSet(event.target.value as BenchFireTransactionSet);
    },
    [],
  );
  const handleStatusChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setStatus214(event.target.value);
    },
    [],
  );
  const handleStopRoleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setStopRole214(event.target.value);
    },
    [],
  );
  const handleActionChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setAction990(event.target.value as Edi990ActionCode);
    },
    [],
  );
  const handleDuplicateChange = useCallback((_: unknown, checked: boolean) => {
    setAllowDuplicate(checked);
  }, []);

  const picksStop =
    transactionSet === BenchFireTransactionSet.Edi_214 &&
    status214 === CONTACT_ATTEMPTED;

  const submit = useCallback(async () => {
    if (order === null) {
      return;
    }
    setError(null);
    setResult(null);
    try {
      const response = await fire({
        variables: {
          input: {
            integrationType: BenchIntegrationType.Tenedi,
            orderUuid: order.uuid,
            transactionSet,
            status214:
              transactionSet === BenchFireTransactionSet.Edi_214 &&
              status214 !== ""
                ? (status214 as Edi214ShipmentStatus)
                : undefined,
            stopRole214:
              picksStop && stopRole214 !== ""
                ? (stopRole214 as BenchStopRole)
                : undefined,
            action990:
              transactionSet === BenchFireTransactionSet.Edi_990
                ? action990
                : undefined,
            allowDuplicate214: allowDuplicate,
            confirmProductionSend,
          },
        },
      });
      throwOnGraphqlErrors(response);
      setResult(response.data?.fireOutboundBenchTransaction ?? null);
    } catch (fireError) {
      setError(
        fireError instanceof Error ? fireError.message : "The send failed.",
      );
    }
  }, [
    action990,
    allowDuplicate,
    confirmProductionSend,
    fire,
    order,
    picksStop,
    status214,
    stopRole214,
    transactionSet,
  ]);

  const handleFireClick = useCallback(() => {
    void submit();
  }, [submit]);

  const needsStatus =
    transactionSet === BenchFireTransactionSet.Edi_214 && status214 === "";

  return (
    <Stack spacing={2}>
      <Box sx={styles.row}>
        <BenchOrderPicker
          label="Order"
          partnershipUuid={partnershipUuid}
          value={order}
          onChange={setOrder}
        />
        <TextField
          select
          label="Transaction"
          value={transactionSet}
          onChange={handleSetChange}
        >
          <MenuItem value={BenchFireTransactionSet.Edi_990}>990</MenuItem>
          <MenuItem value={BenchFireTransactionSet.Edi_214}>214</MenuItem>
          <MenuItem value={BenchFireTransactionSet.Edi_210}>210</MenuItem>
        </TextField>
        {transactionSet === BenchFireTransactionSet.Edi_214 && (
          <TextField
            select
            helperText="From the partnership's 214 list."
            label="214 status"
            value={status214}
            onChange={handleStatusChange}
          >
            {statuses.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </TextField>
        )}
        {picksStop && (
          <TextField
            select
            helperText="Defaults to the delivery, or the pickup when there is none."
            label="Contacted stop"
            value={stopRole214}
            onChange={handleStopRoleChange}
          >
            <MenuItem value="">Default</MenuItem>
            <MenuItem value={BenchStopRole.Inbound}>Pickup</MenuItem>
            <MenuItem value={BenchStopRole.Outbound}>Delivery</MenuItem>
          </TextField>
        )}
        {transactionSet === BenchFireTransactionSet.Edi_990 && (
          <TextField
            select
            label="990 action"
            value={action990}
            onChange={handleActionChange}
          >
            <MenuItem value={Edi990ActionCode.Accepted}>ACCEPTED</MenuItem>
            <MenuItem value={Edi990ActionCode.Cancelled}>CANCELLED</MenuItem>
          </TextField>
        )}
        {transactionSet === BenchFireTransactionSet.Edi_214 && (
          <FormControlLabel
            control={
              <Checkbox
                checked={allowDuplicate}
                onChange={handleDuplicateChange}
              />
            }
            label="Send even if this status already went out"
          />
        )}
        <Button
          disabled={disabled || loading || order === null || needsStatus}
          variant="contained"
          onClick={handleFireClick}
        >
          Send to Tenedi
        </Button>
      </Box>
      {error !== null && <Alert severity="error">{error}</Alert>}
      {result !== null && (
        <Stack spacing={1}>
          <Alert
            severity={
              result.outcome === BenchStepOutcome.Executed
                ? "success"
                : "warning"
            }
          >
            {result.outcome}: {result.message}
          </Alert>
          {result.sideEffects.map((effect) => (
            <Typography key={effect.kind} variant="body2">
              {effect.kind}: {effect.detail}
            </Typography>
          ))}
          <EdiTransactionsTable rows={result.ediTransactions} />
        </Stack>
      )}
    </Stack>
  );
};

export default FirePanel;
